import httpRequest from 'utils/httpRequest';
import { isUrlValid } from 'utils/validator';

/**
 * Send ping message per minute.
 */
const PING_INTERVAL = 1 * 60 * 1000;

/**
 * Maximum timeout is 15 minutes from last message
 */
const MAX_TIMEOUT = 15 * 60 * 1000;

/**
 * Maximum number of retries to reconnect to the socket.
 */
const MAX_NUMBER_OF_SOCKET_CONNECTION_RETRIES = 2;

const MESSAGE_TYPE = {
  PONG: 'pong',
  ERROR: 'error',
  ALIVE: 'alive',
  PROGRESS: 'progress',
  DELAY: 'delayed',
  CLOSED: 'closed',
  DONE: 'done',
  RESULT: 'result',
  FAILED: 'failed',
  UNKNOWN: 'unknown',
};

const MESSAGE = {
  PING: 'ping',
  STATUS: 'status',
  RESULT: 'result',
};

export const fetchAsynchronouslyGeneratedData = async url => {
  try {
    const response = await httpRequest(url, { method: 'GET', retryInAnyCase: true });
    const json = await response.json();
    return { data: json.data };
  } catch (e) {
    return { error: e };
  }
};

const parseMessage = event => {
  const { data } = event;

  // PONG
  if (data === 'pong') {
    return {
      type: MESSAGE_TYPE.PONG,
      data: {},
    };
  }

  try {
    const json = JSON.parse(data);

    // ERROR
    if (json.error) {
      let error;
      try {
        if (typeof json.error === 'string') {
          error = json.error;
        } else {
          error = JSON.stringify(json.error);
        }
      } catch (e) {
        error = 'Unknown error';
      }

      return {
        type: MESSAGE_TYPE.ERROR,
        data: { error },
      };
    }

    // ALIVE
    if (json.status === 'alive') {
      return {
        type: MESSAGE_TYPE.ALIVE,
        data: json,
      };
    }

    // PROGRESS
    if ('total' in json && 'pending' in json) {
      return {
        type: MESSAGE_TYPE.PROGRESS,
        data: json,
      };
    }

    // DELAYED
    // This is a special case for Ancestry, when the sync is delayed due to a large number of requests
    if (json.status === 'delayed') {
      return {
        type: MESSAGE_TYPE.DELAYED,
        data: json,
      };
    }

    // CLOSED
    if (json.status === 'closed') {
      return {
        type: MESSAGE_TYPE.CLOSED,
        data: json,
      };
    }

    // RESULT
    // RESULT is the same as DONE - except resultURL
    if (isUrlValid(json.resultUrl)) {
      return {
        type: MESSAGE_TYPE.RESULT,
        data: json,
      };
    }

    // FAILED
    if (json.status === 'failed') {
      return {
        type: MESSAGE_TYPE.FAILED,
        data: json,
      };
    }

    // DONE
    if (json.status === 'done') {
      return {
        type: MESSAGE_TYPE.DONE,
        data: json,
      };
    }
  } catch (e) {
    return {
      type: MESSAGE_TYPE.ERROR,
      data,
    };
  }

  return {
    type: MESSAGE_TYPE.UNKNOWN,
    data,
  };
};

/**
 * Connects to a WebSocket server to asynchronously receive generated data, with support for progress and delay callbacks, timeout, and retry logic.
 *
 * @async
 * @function getAsynchronouslyGeneratedData
 * @param {string} socketUrl - The WebSocket URL to connect to for receiving data.
 * @param {function({pending: number, total: number}): void} [onProgress=() => {}] - Optional. Callback invoked with progress updates. Receives an object with `pending` and `total` properties.
 * @param {function(delayInSeconds: number): void} [onDelayed=(delayInSeconds) => {}] - Optional. Callback invoked if the process is delayed. Receives the delay in seconds.
 * @param {number} [currentAttempt=0] - Optional. The current retry attempt number. Used internally for retry logic.
 * @returns {Promise<{ data?: object, error?: string }>} Resolves with an object containing the generated data or an error message.
 */
export const getAsynchronouslyGeneratedData = async (
  socketUrl,
  onProgress = () => { },
  onDelayed = (delayInSeconds) => { },
  currentAttempt = 0,
) => {
  try {
    const value = await new Promise((resolve, reject) => {
      const socket = new WebSocket(socketUrl);

      const onSuccess = (data) => {
        resolve(data);
      };

      const onError = error => {
        reject(new Error(error));
      };

      let timeoutId;
      let pingIntervalId;
      let isSyncClosed = false;
      let isDownloading = false;

      // timeout
      const createTimeout = () => {
        timeoutId = setTimeout(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.close(1000);
          }
          onError('Sync time exceeded, please try again later.');
        }, MAX_TIMEOUT);
      };

      //
      // ON OPEN
      //
      socket.onopen = () => {
        // first, ask status
        socket.send(MESSAGE.STATUS);

        // ping/pong heartbeat
        pingIntervalId = setInterval(() => {
          socket.send(MESSAGE.PING);
        }, PING_INTERVAL);

        createTimeout();
      };

      //
      // ON MESSAGE
      //
      socket.onmessage = event => {
        const { type, data } = parseMessage(event);

        if (type === MESSAGE_TYPE.PONG) {
          // do nothing
          return;
        }

        // timeout
        clearTimeout(timeoutId);
        createTimeout();

        switch (type) {
          case MESSAGE_TYPE.ALIVE:
            // do nothing
            return;

          case MESSAGE_TYPE.PROGRESS:
            // progress
            {
              const { pending, total } = data;
              onProgress({ pending, total });
            }
            return;

          case MESSAGE_TYPE.DELAYED: {
            const { delay } = data;
            onDelayed(delay);
            return;
          }

          case MESSAGE_TYPE.CLOSED:
            // sync is closed, ask for result
            isSyncClosed = true;

            clearTimeout(timeoutId);
            clearInterval(pingIntervalId);
            socket.send(MESSAGE.RESULT);
            return;

          case MESSAGE_TYPE.DONE:
            // result received, finish
            socket.close(1000);
            onSuccess(data);
            return;

          case MESSAGE_TYPE.RESULT:
            // result received, fetch data
            socket.close(1000);
            if (!isDownloading) {
              isDownloading = true;
              (async url => {
                const {
                  data: generatedData,
                  error: generatedError,
                } = await fetchAsynchronouslyGeneratedData(url);

                if (generatedError) {
                  onError(
                    `We encountered an [${generatedError.code} - ${generatedError.body}] error while getting your project data, please try again later.`,
                  );
                } else {
                  onSuccess(generatedData);
                }
              })(data.resultUrl);
            }
            return;

          case MESSAGE_TYPE.FAILED:
          case MESSAGE_TYPE.ERROR:
            console.log('WSS error', data);
            // error is useless when sync is not closed
            if (isSyncClosed) {
              socket.close(1000);
              onError(data.error || 'Operation failed');
            }
            return;

          default:
            console.log('WSS [onMessage]', data);
        }
      };

      const retry = async message => {
        if (currentAttempt < MAX_NUMBER_OF_SOCKET_CONNECTION_RETRIES) {
          const { data } = await getAsynchronouslyGeneratedData(
            socketUrl,
            onProgress,
            onDelayed,
            currentAttempt + 1,
          );
          if (data) {
            onSuccess(data);
            return;
          }
        }

        onError(
          `We encountered a connection error (${message}) while syncing your family history data, please try again.`,
        );
      };

      //
      // ON CLOSE
      // - clear timeout and pingInterval
      //
      socket.onclose = async event => {
        clearTimeout(timeoutId);
        clearInterval(pingIntervalId);

        if (event.code !== 1000) {
          console.error('WSS - onclose', event);
          retry(event.reason ?? 'WebSocket Closed');
        }
      };

      //
      // ON ERROR
      // - clear timeout and pingInterval
      // - retry
      //
      socket.onerror = async event => {
        clearTimeout(timeoutId);
        clearInterval(pingIntervalId);

        console.error('WSS - onerror', event);
        retry(event.message ?? event.type ?? 'WebSocket Error');
      };
    });

    return { data: value };
  } catch (e) {
    return { error: e.message };
  }
};
